import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/DashboardLayout";
import ClientHome from "@/pages/client/ClientHome";
import NewRequest from "@/pages/client/NewRequest";
import RequestDetail from "@/pages/RequestDetail";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminClients from "@/pages/admin/AdminClients";
import AdminStaff from "@/pages/admin/AdminStaff";
import Settings from "@/pages/Settings";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PermGate({ perm, adminOnly, children }) {
  const { user } = useAuth();
  const allowed = user && (user.role === "admin" || (!adminOnly && (user.permissions || []).includes(perm)));
  if (allowed) return children;
  return (
    <div className="mx-auto mt-10 max-w-md rounded-xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="access-denied">
      <h2 className="font-display text-xl font-bold text-amber-800">Accès refusé</h2>
      <p className="mt-2 text-sm text-amber-700">Vous n'avez pas l'accès requis pour cette section. Contactez votre administrateur.</p>
    </div>
  );
}

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-10 w-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Handle Google OAuth callback synchronously (before ProtectedRoute runs)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<ClientHome />} />
        <Route path="nouvelle-demande" element={<NewRequest />} />
        <Route path="demande/:id" element={<RequestDetail />} />
        <Route path="admin" element={<PermGate perm="analytics"><AdminOverview /></PermGate>} />
        <Route path="admin/demandes" element={<PermGate perm="requests"><AdminRequests /></PermGate>} />
        <Route path="clients" element={<PermGate perm="clients"><AdminClients /></PermGate>} />
        <Route path="staff" element={<PermGate adminOnly><AdminStaff /></PermGate>} />
        <Route path="reglages" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
